import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getProjectById,
  getCapabilityForProject,
  getTestimonialsForProject,
  getTestimonialParagraphs,
  getAdjacentProjects,
  getClientInitials,
  getRelatedProject
} from '../utils/dataModel';
import Modal from './Modal';
import ContactForm from './ContactForm';
import SEO from './SEO';

const WorkPage = () => {
  const { projectId } = useParams();
  const project = getProjectById(projectId);
  const capability = project ? getCapabilityForProject(projectId) : null;
  const testimonials = project ? getTestimonialsForProject(projectId) : [];
  const { previous, next } = getAdjacentProjects(projectId);
  const related = getRelatedProject(projectId);
  const [showContactModal, setShowContactModal] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLogoFailed(false);
  }, [projectId]);

  if (!project) {
    return (
      <>
        {/* The SPA fallback serves this with a 200, so without noindex a mistyped or
            stale /work/<id> becomes an indexable page claiming to be the homepage. */}
        <SEO
          title="Project Not Found | Alek Hagopian"
          description="This project page does not exist."
          noindex
        />
        <div className="project-page not-found">
          <div className="container">
            <h1>Project Not Found</h1>
            <p>That project doesn't exist — it may have moved.</p>
            <Link to="/work" className="btn btn-primary">See all work</Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${project.title} — ${project.client_name} | Alek Hagopian`}
        description={project.headline_outcome
          ? `${project.headline_outcome}. ${project.problem_statement}`
          : project.problem_statement}
      />

      <div className="project-page">
        <div className="project-header">
          <div className="container">
            <div className="project-breadcrumb">
              <Link to="/">Home</Link>
              <span> / </span>
              <Link to="/work">Work</Link>
              <span> / </span>
              <span>{project.client_name}</span>
            </div>

            <div className="project-header-content">
              <div className="project-logo-large">
                {logoFailed ? (
                  <div className="project-logo-initials" aria-hidden="true">
                    {getClientInitials(project.client_name)}
                  </div>
                ) : (
                  <img
                    src={project.logo}
                    alt={`${project.client_name} logo`}
                    onError={() => setLogoFailed(true)}
                  />
                )}
              </div>
              <div className="project-header-text">
                <h1>{project.title}</h1>
                <p className="project-client-large">{project.client_name}</p>
                {/* The card on /work promises this result; it should not disappear
                    on the page the visitor clicked through to read. */}
                {project.headline_outcome && (
                  <p className="project-outcome-large">{project.headline_outcome}</p>
                )}
                {capability && (
                  <div className="project-service-tag">
                    <Link to={`/work#${capability.id}`}>{capability.title}</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="project-content">
          <div className="container">
            <section className="project-overview">
              <div className="overview-grid">
                <div className="problem-statement">
                  <h2>The Challenge</h2>
                  <p>{project.problem_statement}</p>
                </div>

                <div className="solution-overview">
                  {/* Not every engagement was a build — Mastercard was staffing and
                      mentoring — so the heading is overridable per project. */}
                  <h2>{project.solution_heading || 'What I Built'}</h2>
                  <p>{project.solution}</p>
                </div>
              </div>
            </section>

            <section className="project-details">
              <h2>What I Delivered</h2>
              <ul className="details-list">
                {project.details.map((detail, index) => (
                  <li key={index} className="detail-item">
                    <span className="detail-icon">✓</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </section>

            {/* Hart Energy and Crain are one continuous engagement across an
                acquisition. Presented as unrelated case studies, the strongest fact
                on either page — that the acquirer kept me on — disappears. */}
            {related && (
              <aside className="project-thread">
                <p className="project-thread-label">{related.label}</p>
                <p className="project-thread-note">{related.note}</p>
                <Link to={`/work/${related.project.id}`} className="project-thread-link">
                  {related.project.client_name}: {related.project.title} →
                </Link>
              </aside>
            )}

            {testimonials.length > 0 && (
              <section className="project-testimonials">
                <h2>What They Said</h2>
                <div className="testimonials-list">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="testimonial-card">
                      <div className="testimonial-content">
                        {getTestimonialParagraphs(testimonial).map((paragraph, index, all) => (
                          <p key={index} className="testimonial-body">
                            {index === 0 ? '"' : ''}{paragraph}{index === all.length - 1 ? '"' : ''}
                          </p>
                        ))}
                      </div>
                      <div className="testimonial-author">
                        <img
                          src={testimonial.picture}
                          alt={testimonial.name}
                          className="testimonial-picture"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="testimonial-info">
                          <p className="testimonial-name">{testimonial.name}</p>
                          <p className="testimonial-title">{testimonial.title}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Without this every case study was a dead end but for "See more work". */}
            {(previous || next) && (
              <nav className="project-pager" aria-label="More projects">
                {previous && (
                  <Link to={`/work/${previous.id}`} className="project-pager-link is-previous">
                    <span className="project-pager-direction">← Previous</span>
                    <span className="project-pager-title">{previous.title}</span>
                    <span className="project-pager-client">{previous.client_name}</span>
                  </Link>
                )}
                {next && (
                  <Link to={`/work/${next.id}`} className="project-pager-link is-next">
                    <span className="project-pager-direction">Next →</span>
                    <span className="project-pager-title">{next.title}</span>
                    <span className="project-pager-client">{next.client_name}</span>
                  </Link>
                )}
              </nav>
            )}

            <section className="project-cta">
              <div className="cta-content">
                <h2>Have something similar in front of you?</h2>
                <p>Tell me what you're trying to build and I'll tell you how I'd approach it.</p>
                <div className="cta-buttons">
                  <button className="btn btn-primary" onClick={() => setShowContactModal(true)}>
                    Work with me →
                  </button>
                  <Link to="/work" className="btn btn-secondary">See all work</Link>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Start a conversation"
      >
        <ContactForm onClose={() => setShowContactModal(false)} />
      </Modal>
    </>
  );
};

export default WorkPage;
